import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import uuid from 'uuid/v4';

import { withResponsive } from 'mage-components';
import { components as clientComponents } from 'mage-client';
import { analytics } from 'moet-core';

import { CAPS, FEATURES, REGIONS, IS_ONBOARDING_APP } from '@/constants';
import { QUEUE_NAME } from '@/constants/fresh-chat';
import { isParticipatingInFreshChat } from '@/selectors/experimentation';
import { getWidgetTypeDefaultUnmanaged } from '@/selectors/widget';
import { isWorkingHours, getGroupId, shouldShowChat, getQueueName } from '@/selectors/freshChat';
import { getUserData } from '@/selectors/data';
import {
  getMerchant,
  getMerchantCountryKey,
  getUserMerchantId,
  isNewSplitLayout,
  merchantRegion,
} from '@/selectors/merchant';

import { initFreshChat, setExternalId } from './freshChat';
import { ANALYTICS_EVENTS } from './analyticsEvents';

const FRESH_CHAT_EXPERIMENT_SESSION_ID = 'FRESH_CHAT_EXPERIMENT_SESSION_ID';

/** returns the session ID for the experiment, not the fresh chat widget */
const freshChatSessionId = () => {
  let sessionId = window.localStorage.getItem(FRESH_CHAT_EXPERIMENT_SESSION_ID);

  if (!sessionId) {
    sessionId = uuid();
    window.localStorage.setItem(FRESH_CHAT_EXPERIMENT_SESSION_ID, sessionId);
  }

  return sessionId;
};

const registerFreshChatEvents = () => {
  // https://developers.freshchat.com/web-sdk/#messenger-events
  window.fcWidget.on('widget:loaded', () => {
    // https://docs.mparticle.com/developers/sdk/web/users/#set-user-tags
    analytics.setUserTag('FRESH_CHAT');
  });

  window.fcWidget.on('widget:opened', () => {
    analytics.logEvent(ANALYTICS_EVENTS.widgetOpened);
  });

  window.fcWidget.on('widget:closed', () => {
    analytics.logEvent(ANALYTICS_EVENTS.widgetClosed);
  });

  window.fcWidget.on('user:created', res => {
    /**
     * NOTE
     * this property will be available after user sends the first chat message
     * this means we may publish this event more than once if the page is reloaded
     */
    if (res.data) {
      analytics.logEvent(ANALYTICS_EVENTS.chatInitiated);
    }
  });
};

const FreshChatWidgetComponent = props => {
  const {
    email,
    freshChatOperatingHours,
    groupId,
    isDesktop,
    isMobile,
    isNewSplitLayout,
    isParticipatingInFreshChat,
    isWorkingHours,
    merchant,
    merchantCountry,
    merchantId,
    participate,
    region,
    shouldShowChat,
    queueName,
  } = props;
  const adjustWidgetPosition = useCallback(() => {
    const fcFrame = document.getElementById('fc_frame');

    if (fcFrame) {
      if (isDesktop && isNewSplitLayout) {
        fcFrame.style.right = 'calc(50% + 15px)';
      } else {
        // Go back to default
        fcFrame.style.right = null;
      }

      if (!isMobile && merchantId) {
        fcFrame.style.bottom = '55px';
      } else {
        // Go back to default
        fcFrame.style.bottom = null;
      }

      fcFrame.style['z-index'] = 200; // by default fresh chat will have a huge z-index
    }
  }, [window.fcWidget, isDesktop, isMobile, isNewSplitLayout, merchantId]);

  useEffect(() => {
    adjustWidgetPosition();
  }, [window.fcWidget, isDesktop, isMobile, merchantId]);

  useEffect(() => {
    if (groupId && shouldShowChat) {
      freshChatOperatingHours({ params: { groupId }, region });
    }
  }, [groupId, shouldShowChat]);

  useEffect(() => {
    if (isWorkingHours) {
      const body = { featureKey: FEATURES.FRESH_CHAT, identifier: freshChatSessionId() };
      participate({ body, region });
    }
  }, [isWorkingHours]);

  useEffect(() => {
    if (isParticipatingInFreshChat && isWorkingHours && shouldShowChat) {
      if (!window.fcWidget?.isInitialized()) {
        const isInReview = queueName === QUEUE_NAME.REVIEW;
        initFreshChat({
          externalId: email,
          ...(isInReview ? { tags: ['merchant-review'] } : {}),
          callback: () => {
            adjustWidgetPosition();
            window.fcWidget.on('widget:closed', adjustWidgetPosition);
            registerFreshChatEvents();
            isInReview && window.fcWidget.open();
          },
          country: merchantCountry,
          region,
        });
      }
    } else {
      /* eslint-disable-next-line no-unused-expressions */
      window.fcWidget?.destroy();
    }
  }, [groupId, queueName, isParticipatingInFreshChat, isWorkingHours, merchant, shouldShowChat]);

  useEffect(() => {
    if (email && IS_ONBOARDING_APP) {
      setExternalId(email);
    }
  }, [email]);

  return null;
};

FreshChatWidgetComponent.propTypes = {
  freshChatOperatingHours: PropTypes.func.isRequired,
  isDesktop: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isNewSplitLayout: PropTypes.bool.isRequired,
  isParticipatingInFreshChat: PropTypes.bool.isRequired,
  isWorkingHours: PropTypes.bool.isRequired,
  merchant: PropTypes.object.isRequired,
  merchantCountry: PropTypes.string.isRequired,
  participate: PropTypes.func.isRequired,
  region: PropTypes.oneOf(Object.values(REGIONS)).isRequired,
  shouldShowChat: PropTypes.bool.isRequired,
  widgetType: PropTypes.string.isRequired /* eslint-disable-line react/no-unused-prop-types */,
  email: PropTypes.string,
  groupId: PropTypes.number,
  merchantId: PropTypes.string,
  queueName: PropTypes.oneOf(Object.values(QUEUE_NAME)),
};

export const FreshChatWidget = compose(
  clientComponents.withPromiseBasedCapabilities({
    participate: CAPS.EXPERIMENT,
    freshChatOperatingHours: CAPS.FRESH_CHAT_OPERATING_HOURS,
  }),
  connect(state => ({
    email: getUserData(state).email || getMerchant(state).email,
    groupId: getGroupId(state),
    isNewSplitLayout: isNewSplitLayout(state),
    isParticipatingInFreshChat: isParticipatingInFreshChat(state),
    isWorkingHours: isWorkingHours(state),
    shouldShowChat: shouldShowChat(state),
    widgetType: getWidgetTypeDefaultUnmanaged(state),
    merchant: getMerchant(state),
    merchantId: getUserMerchantId(state),
    merchantCountry: getMerchantCountryKey(state),
    region: merchantRegion(state),
    queueName: getQueueName(state),
  })),
  withResponsive,
)(FreshChatWidgetComponent);
